import { inject, Injectable, LOCALE_ID } from '@angular/core';
import {
  HttpEvent,
  HttpResponse,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse
} from '@angular/common/http';
import { finalize, Observable, TimeoutError } from 'rxjs';
import { map, catchError, timeout } from 'rxjs/operators';

import { SupportedLocale, environment } from '@env';

import { Notifier } from '../notifications';
import { isEmpty, isString } from '../helpers';

import { SSR_API_HOST } from './express.tokens';
import { isCaptchaResponse, isServerResponse } from './helpers';
import { ERROR_CALLBACK, THROW_ERROR } from './api.interface';
import { HttpErrorFormater, FALLBACK_ERROR_TEXT } from './http-error-formatter';
import { ServerTimingService } from './server-timing.service';

const REQUEST_TIMEOUT = 5;
const TIMEOUT_MESSAGE = $localize`:@@system.request_timeout:Превышено время отправки запроса`;

@Injectable({
  providedIn: 'root'
})
export class ApiRequestInterceptor<T = any> implements HttpInterceptor {
  private readonly localeId = inject<SupportedLocale>(LOCALE_ID);
  private readonly ssrApiHost = inject(SSR_API_HOST, { optional: true });
  private readonly serverTiming = inject(ServerTimingService);
  private readonly notifier = inject(Notifier);
  private readonly errorFormatter = inject(HttpErrorFormater);

  intercept(request: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    const requestUrl = request.url;
    const responseTimingName = String(Date.now());

    if(!this.isPrefixed(requestUrl)) {      
      request = request.clone({
        url: environment.apiPath + requestUrl,
        headers: request.headers.set('Accept-Language', this.localeId)
      });
    }

    if(this.ssrApiHost) {
      const originalUrl = request.url;

      request = request.clone({
        url: `${ this.ssrApiHost }${ originalUrl }`
      });

      this.serverTiming.start(responseTimingName, `${ request.method } ${ originalUrl }`);
    }

    const handler = next.handle(request) as Observable<HttpEvent<T>>;

    if(REQUEST_TIMEOUT > 0) {
      handler.pipe(timeout(REQUEST_TIMEOUT * 1000));
    }

    return handler.pipe(
      map((event: HttpEvent<T>) => {
        if(event instanceof HttpResponse && isServerResponse(event.body)) {
          return event.clone({
            body: event.body.data
          });
        }

        return event;
      }),
      catchError((error: any) => {
        if(error instanceof TimeoutError) {
          this.showTimeoutError();
        }
        
        const throwError = request.context.get(THROW_ERROR);
        const errorCallback = request.context.get(ERROR_CALLBACK);

        if(throwError && !isCaptchaResponse(error.error)) {
          let formattedError: string;

          if(error instanceof HttpErrorResponse) {
            formattedError = this.errorFormatter.getFormattedError(error).format();
          } else if(isString(error)) {
            formattedError = error;
          } else if(error instanceof Error) {
            formattedError = error.message;
          } else {
            formattedError = FALLBACK_ERROR_TEXT;
          }

          this.notifier.error(formattedError);

          errorCallback && errorCallback();
        }

        throw error;
      }),
      finalize(() => this.serverTiming.end(responseTimingName))
    );
  }

  private isPrefixed(url: string): boolean {
    return /^(http|https):/i.test(url) ||
      (!isEmpty(environment.apiPath) && url.startsWith(environment.apiPath)) ||
      (!isEmpty(environment.cdnPath) && url.startsWith(environment.cdnPath));
  }

  private showTimeoutError(): void {
    this.notifier.error(TIMEOUT_MESSAGE);
  }
}